import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import ActionsGrid from 'components/ActionsGrid'
import UserModal from 'components/UserModal'
import {
  selectCurrentUser,
  selectIsLoadingUsers,
  selectUsers,
  setIsInitialized,
  addAlert,
  fetchCurrentUser,
} from 'store/global'
import { selectDisplayedUser, setDisplayedUser, submitUser, updateUser, deleteUser, closeDisplayedUser } from 'store/organizations'
import messages from './messages'
import styles from './Users.module.scss'
import Input from 'components/Input'
import { orderBy } from 'lodash'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { saveAs } from 'file-saver'
import { Parser } from 'json2csv'
import Button from 'components/Button'
import Icon from 'components/Icon'
import Loader from 'components/Loader'

import { gridColumns, COLUMN_ORGANIZATION, COLUMN_ROLE, COLUMN_GROUPS, COLUMN_ACTIONS } from './specs'

export default function Users() {
  const dispatch = useDispatch()

  const currentUser = useSelector(selectCurrentUser)
  const isLoading = useSelector(selectIsLoadingUsers)
  const users = useSelector(selectUsers)
  const displayedUser = useSelector(selectDisplayedUser)

  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')

  const sortableColumns = new Set(['firstName', 'lastName', 'email', 'organization', 'role', 'groups'])

  useEffect(() => {
    dispatch(closeDisplayedUser())
    dispatch(setIsInitialized(true))
  }, [])

  const onSort = column => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const filteredUsers = React.useMemo(() => {
    if (!searchTerm) return users
    return users.filter(user =>
      [
        user.firstName,
        user.lastName,
        user.email,
        user.organization,
        user.role,
        user.groups
      ].some(value => value?.toString().toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [users, searchTerm])

  const sortedUsers = React.useMemo(() => {
    if (!sortColumn) return filteredUsers
    return orderBy(filteredUsers, [sortColumn], [sortOrder])
  }, [filteredUsers, sortColumn, sortOrder])

  const onDisplayModal = (action = 'create', existingUser = {}) => {
    const newUser = {
      ...existingUser,
      action,
      isOpen: true,
    }

    dispatch(setDisplayedUser({ user: newUser }))
  }

  const onSubmit = async user => {
    try {
      if (displayedUser.action === 'delete') {
        if (displayedUser.email === currentUser.email) {
          await dispatch(
            addAlert({
              type: 'danger',
              message: `User ${displayedUser.email} cannot delete itself`,
            }),
          )
          dispatch(setDisplayedUser())
          return
        }
        await dispatch(deleteUser({ email: displayedUser.email }))
      } else if (displayedUser.action === 'edit') {
        const updatedUser = {
          ...user,
          id: displayedUser.id,
          organization: user.organization || currentUser.organizationId,
          role: user.role || 1,
          groupIds: user.groupIds || [],
        }
        await dispatch(updateUser({ user: updatedUser }))
      } else {
        const submittedUser = {
          ...user,
          organization: user.organization || currentUser.organizationId,
          role: user.role || 'basic-user',
          groupIds: user.groupIds || [],
        }
        await dispatch(submitUser({ user: submittedUser }))
      }

      dispatch(addAlert({ type: 'success', message: 'User Action Performed' }))
      dispatch(setDisplayedUser())
      dispatch(fetchCurrentUser())
    } catch (error) {
      dispatch(addAlert({ type: 'error', message: 'User Action Failed' }))
    }
  }

  const onExportCSV = () => {
    if (!sortedUsers.length) return
    
    const exportData = sortedUsers.map(user => ({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      organization: user.organization || '',
      role: user.role || '',
      groups: user.groups || '',
      email: user.email || ''
    }))
    
    const fields = ['firstName', 'lastName', 'organization', 'role', 'groups', 'email']
    const parser = new Parser({ fields })
    const csv = parser.parse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'users.csv')
  }

  const renderGridCell = (user, column) => {
    if (column === COLUMN_ORGANIZATION) {
      return user.organization || ''
    }
    if (column === COLUMN_ROLE) return user.role || ''
    if (column === COLUMN_GROUPS) return user.groups || ''
    if (column === COLUMN_ACTIONS) {
      // Check if user is managed (cannot be edited/deleted)
      const isManaged = user.membershipType === 'MANAGED'
      
      if (isManaged) {
        return (
          <div className={styles.actions}>
            <span className={styles.managedMessage}>
              <FormattedMessage {...messages.actions.managedByOrganization} />
            </span>
          </div>
        )
      }
      
      return (
        <div className={styles.actions}>
          <Button 
            color='primary' 
            outline 
            onClick={() => onDisplayModal('edit', user)}
          >
            <Icon name='edit' size='regular'>
              <FormattedMessage {...messages.actions.edit} />
            </Icon>
          </Button>
          <div className={styles.buttonGap}></div>
          <Button 
            color='danger' 
            outline 
            onClick={() => onDisplayModal('delete', user)}
          >
            <Icon name='delete' size='regular'>
              <FormattedMessage {...messages.actions.delete} />
            </Icon>
          </Button>
        </div>
      )
    }
    return user[column] || ''
  }

  const displayedGridColumns = gridColumns.filter(({ isDisplayed = () => true }) => isDisplayed(currentUser))

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <Loader type='globalLoading' message='Loading users...' />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <Input
        placeholder="Input search text..."
        value={searchTerm}
        onChange={value => setSearchTerm(value)}
        className={styles.search}
      />

      <ActionsGrid
        isLoading={isLoading}
        type="user"
        rows={sortedUsers}
        columns={displayedGridColumns}
        header={<FormattedMessage {...messages.header} />}
        onAction={onDisplayModal}
        onSort={onSort}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onExportCSV={onExportCSV}
        showCreateButton={false}
      >
        {(user, columnKey, isHeader) =>
          isHeader ? (
            <div>
              {sortableColumns.has(columnKey) ? (
                <div className={styles.header} onClick={() => onSort(columnKey)}>
                  <FormattedMessage {...messages.columns[columnKey]} /> <SwapVertIcon fontSize="small" />
                </div>
              ) : (
                <FormattedMessage {...messages.columns[columnKey]} />
              )}
            </div>
          ) : (
            renderGridCell(user, columnKey)
          )
        }
      </ActionsGrid>
      {!!displayedUser.isOpen && <UserModal mode={displayedUser.action} onSubmit={onSubmit} />}
    </div>
  )
}
